import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashHeader, GoogleButton } from "@/components/account";
import { useSeo } from "@/lib/seo";
import "@/styles/account.css";

export default function TesterDashboard() {
  useSeo({ title: "Tester dashboard", path: "/tester", noindex: true });
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);

  async function emailVerifyLink() {
    setSending(true);
    try {
      await api.resendVerification();
      toast.success(`Verification link sent to ${user.email}. Click it, then reopen this page.`);
    } catch (e) {
      toast.error("Could not send the link right now. Please try again shortly.");
    } finally {
      setSending(false);
    }
  }

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    api.accountTester().then(setData).catch((e) => setErr(e?.response?.status === 403 ? "forbidden" : "error"));
  }, [user, loading, navigate]);

  if (loading || (!data && !err)) {
    return (<><DashHeader /><div className="dash-wrap"><p className="muted">Loading…</p></div></>);
  }
  if (err === "forbidden") {
    return (<><DashHeader /><div className="dash-wrap" data-testid="tester-forbidden"><div className="dash-head"><h1>Tester dashboard</h1></div><p className="muted">This dashboard is for tester accounts. <a className="link" href="/dashboard">Go to your buyer dashboard</a>.</p></div></>);
  }
  if (err) {
    return (<><DashHeader /><div className="dash-wrap"><p className="muted">Could not load your tester dashboard.</p></div></>);
  }

  const t = data.tester;
  if (data.needs_verification) {
    return (
      <>
        <DashHeader />
        <div className="dash-wrap" data-testid="tester-needs-verification">
          <div className="dash-head"><h1>Tester dashboard</h1></div>
          <p className="muted">To protect tester payout details, we only link your tester profile after we've verified you own <b>{user.email}</b>. Verify once and your profile unlocks automatically.</p>
          <div className="verify-actions" style={{ marginTop: 16 }}>
            <button type="button" className="btn blue" onClick={emailVerifyLink} disabled={sending} data-testid="tester-email-verify">
              {sending ? "Sending…" : "Email me a verification link"}
            </button>
            <GoogleButton label="Or verify with Google" role="tester" />
          </div>
          <p className="muted small" style={{ marginTop: 16 }}>Click the link we email you, then reopen this page. New here? <a className="link" href="/join" data-testid="tester-join">Become a tester</a></p>
        </div>
      </>
    );
  }
  if (!t) {
    return (
      <>
        <DashHeader />
        <div className="dash-wrap" data-testid="tester-unlinked">
          <div className="dash-head"><h1>Tester dashboard</h1></div>
          <p className="muted">We couldn't find a tester profile for <b>{user.email}</b> yet. If you've applied, we'll link it once your account is activated. New here?</p>
          <a className="btn blue" href="/join" data-testid="tester-join">Become a tester</a>
        </div>
      </>
    );
  }

  const s = data.stats || {};
  const paiseToInr = (p) => money(p || 0, "INR");

  return (
    <>
      <DashHeader />
      <div className="dash-wrap" data-testid="tester-dashboard">
        <div className="dash-head">
          <h1>{t.name || user.email}</h1>
          <p className="muted">{t.city ? `${t.city} · ` : ""}Band {t.band} · {t.state}{t.upi ? ` · ${t.upi}` : ""}</p>
        </div>

        <div className="dash-cards">
          <div className="dash-card" data-testid="t-card-sessions"><span className="k">Sessions</span><b>{s.sessions || 0}</b></div>
          <div className="dash-card" data-testid="t-card-submitted"><span className="k">Submitted</span><b>{s.submitted || 0}</b></div>
          <div className="dash-card" data-testid="t-card-pending"><span className="k">Pending payout</span><b>{paiseToInr(s.pending_paise)}</b></div>
          <div className="dash-card" data-testid="t-card-paid"><span className="k">Paid out</span><b>{paiseToInr(s.paid_paise)}</b></div>
        </div>

        {t.devices?.length > 0 && (
          <section className="dash-sec">
            <div className="dash-sec-head"><h2>Your devices</h2></div>
            <div className="chips" data-testid="tester-devices">
              {t.devices.map((d, i) => (
                <span className="chip" key={i}>{d.brand || d.os || d.platform} {d.model || d.browser || ""}{d.os_version ? ` · ${d.os_version}` : ""}</span>
              ))}
            </div>
          </section>
        )}

        <section className="dash-sec">
          <div className="dash-sec-head"><h2>Recent sessions</h2></div>
          {(!data.sessions || data.sessions.length === 0) ? (
            <p className="muted" data-testid="tester-sessions-empty">No sessions yet.</p>
          ) : (
            <div className="dash-table-wrap">
              <table className="dash-table" data-testid="tester-sessions">
                <thead><tr><th>Band</th><th>Status</th><th>Device</th><th>When</th></tr></thead>
                <tbody>
                  {data.sessions.map((ss) => (
                    <tr key={ss.id}>
                      <td>{ss.band || "—"}</td>
                      <td><span className="state">{ss.state}</span></td>
                      <td className="muted small">{ss.device_used || "—"}</td>
                      <td className="muted small">{ss.created_at ? new Date(ss.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="dash-sec">
          <div className="dash-sec-head"><h2>Payouts</h2></div>
          {(!data.payouts || data.payouts.length === 0) ? (
            <p className="muted" data-testid="tester-payouts-empty">No payouts yet.</p>
          ) : (
            <div className="dash-table-wrap">
              <table className="dash-table" data-testid="tester-payouts">
                <thead><tr><th>Amount</th><th>Status</th><th>Reference</th><th>When</th></tr></thead>
                <tbody>
                  {data.payouts.map((p, i) => (
                    <tr key={i}>
                      <td>{paiseToInr(p.amount_paise)}</td>
                      <td><span className="state">{p.state}</span></td>
                      <td className="muted small">{p.reference || "—"}</td>
                      <td className="muted small">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}
